import { generateGuestOfferCodeEndpoint, generateGuestOffersEndpoint, useAppQuery, useAppMutation, queryKeyFactory, OfferTypeId, Offer } from '@alluradirect/react';
import { useQueryClient } from 'react-query';
import { formatCanadianCurrency } from 'helpers/currencyHelpers';
import { useCheckoutParams } from 'components/views/pages/Checkout/hooks/useCheckoutParams';
import { useLoginState } from 'components/views/pages/_general/useLoginState';


interface queryArgs {
  onSuccess?(data?: any): void;
  enabled?: boolean;
}


export const getOfferValueDisplay = (offer: Offer) => {
  if( offer !== undefined && offer != '' ){
    const { OFFER_TYPE_ID, DOLLAR, CODE } = offer;
    const codeDisplay = `(${CODE})`;
    switch (OFFER_TYPE_ID) {
      case OfferTypeId.PERCENTAGE:
        return `Save ${offer.PERCENTAGE}% ${codeDisplay}`;
      case OfferTypeId.DOLLAR:
        return `Save ${formatCanadianCurrency(DOLLAR)} ${codeDisplay}`;
      case OfferTypeId.FREE_NIGHTS:
        return `Save ${offer.NIGHTS_FREE} free nights ${codeDisplay}`;
      case OfferTypeId.GIFT_CERTIFICATE:
        return DOLLAR ? `${formatCanadianCurrency(DOLLAR)} value` :`Gift/Gift Certificate`;
      default:
        return '';
    }
  } else {
    return '';
  }
}

export type OfferMutationResponse = {
  DATA: {
    OFFERS: {
      ALLURA?: "" | Offer,
      OWNER?: "" | Offer,
    }
    GIFT_CERTIFICATES: {
      ALLURA?: "" | Offer,
      OWNER?: "" | Offer,
    }
  }
  ERRORS: {
    CLIENT_MESSAGE?: string;
  }
}

const guestOfferCodeQueryKey = queryKeyFactory('guest-offer-code');
export const useGuestOfferCodeMutation = ({
  onSuccess = () => null,
}: queryArgs) => {
  const { propertyId, startDate, endDate } = useCheckoutParams();
  const { userId } = useLoginState({ propertyId });
  const endpoint = generateGuestOfferCodeEndpoint(userId,propertyId,startDate,endDate);
  const queryKey = guestOfferCodeQueryKey.detail(endpoint);

  const queryClient = useQueryClient();
  return useAppMutation<unknown, OfferMutationResponse>({
    method: 'GET',
    url: (code: string) => `${endpoint}&code=${code}`,
    onSuccess: async () => {
      await queryClient.refetchQueries(queryKey);
      onSuccess();
    },
  });
};

export type PublicOffersResponse = {
  DATA: {
    OFFERS: {
      OWNER?: Offer[],
      ALLURA?: Offer[],
    },
    GIFT_CERTIFICATES: {
      OWNER?: Offer[],
      ALLURA?: Offer[],
    } 
  }
}

const publicOffersQueryKey = queryKeyFactory('guest-offers');
export const usePublicOffersQuery = ({
  onSuccess = () => null,
}: {
  onSuccess?: (data?: PublicOffersResponse) => void; 
}) => {
  const { propertyId, startDate, endDate } = useCheckoutParams();
  const { userId } = useLoginState({ propertyId });
  const endpoint = generateGuestOffersEndpoint(userId,propertyId,startDate,endDate);
  const queryKey = publicOffersQueryKey.detail(endpoint);

  return useAppQuery<PublicOffersResponse>({
    endpoint,
    queryKey,
    onSuccess,
  });
};
